import React, { useEffect, useState } from 'react';
import { WebSocketAdapter } from '../../utils/WebSocketAdapter';
import Header from '../../components/shared/Header';
import { GameEndScreen } from '../../components/game-screens/GameEndScreen';
import { useGameEndScreenData } from '../../hooks/useGameEndScreenData';
import { DrawingCanvas, StrokeReplay, DoodleStroke } from '../_shared/DrawingCanvas';
import './doodleHeist.css';
import '../../App.css';
import '../../styles/player-screen.css';

interface DoodlePlayerState {
  phase: string;
  role?: string;
  artId?: string;
  prompt?: string;
  strokes?: DoodleStroke[];
  options?: Array<{ id: string; letter: string; text: string; mine?: boolean }>;
  alreadySubmitted?: boolean;
  startTime?: number;
  timeLimit?: number;
  banner?: string;
  promptReveal?: string;
  artistName?: string;
  standings?: Array<{ playerId: string; playerName: string; score: number }>;
}

interface Props {
  roomState: {
    roomName: string;
    gameType: string;
    hostId: string;
    players: Array<{ id: string; name: string; score: number; avatar?: string }>;
    gameState: 'waiting' | 'playing' | 'finished';
  };
  socket: WebSocketAdapter;
  myPlayerId: string;
  onGoHome?: () => void;
  isHost?: boolean;
  onBackToGameSelection?: () => void;
  onLoadingTimeout?: () => void;
  isBonusLevel?: boolean;
}

const DoodleHeistPlayerDisplay: React.FC<Props> = ({
  roomState,
  socket,
  myPlayerId,
  onGoHome,
  onBackToGameSelection,
}) => {
  const [state, setState] = useState<DoodlePlayerState | null>(null);
  const [fakeText, setFakeText] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gameEnded, setGameEnded] = useState(false);
  const [finalLeaderboard, setFinalLeaderboard] = useState<
    Array<{ playerId: string; playerName: string; score: number }>
  >([]);
  const gameEndData = useGameEndScreenData(roomState);

  useEffect(() => {
    if (roomState.gameState === 'finished' && gameEndData) {
      setGameEnded(true);
      setFinalLeaderboard(gameEndData.finalLeaderboard);
    }
  }, [roomState.gameState, gameEndData]);

  useEffect(() => {
    const onState = (data: DoodlePlayerState) => {
      setState(data);
      setError(null);
      setSubmitted(!!data.alreadySubmitted);
      if (data.phase !== 'fake') setFakeText('');
    };
    const onAck = () => setSubmitted(true);
    const onErr = (data: { message?: string }) => setError(data.message || 'Error');
    const onEnded = (data: {
      leaderboard?: Array<{ playerId: string; playerName: string; score: number }>;
    }) => {
      setGameEnded(true);
      setFinalLeaderboard(data.leaderboard || []);
    };

    socket.on('doodle-heist-state', onState);
    socket.on('doodle-heist-ack', onAck);
    socket.on('doodle-heist-error', onErr);
    socket.on('game-ended', onEnded);
    socket.emit('get-doodle-heist-state', { roomName: roomState.roomName });

    return () => {
      socket.off('doodle-heist-state', onState);
      socket.off('doodle-heist-ack', onAck);
      socket.off('doodle-heist-error', onErr);
      socket.off('game-ended', onEnded);
    };
  }, [socket, roomState.roomName]);

  if (gameEnded) {
    return (
      <GameEndScreen
        finalLeaderboard={finalLeaderboard}
        gameStatistics={gameEndData?.gameStatistics ?? null}
        onPlaySameGame={() => {
          socket.emit('start-game', { roomName: roomState.roomName, gameType: roomState.gameType });
          setGameEnded(false);
        }}
        onPlayAnotherGame={onBackToGameSelection || (() => {})}
        onBackToGameSelection={onBackToGameSelection}
        roomName={roomState.roomName}
        gameType={roomState.gameType}
      />
    );
  }

  const submitDrawing = (strokes: DoodleStroke[]) => {
    if (!state?.artId || submitted) return;
    socket.emit('doodle-heist-drawing', {
      roomName: roomState.roomName,
      artId: state.artId,
      strokes,
    });
  };

  const submitFake = () => {
    if (!fakeText.trim() || submitted) return;
    socket.emit('doodle-heist-fake', { roomName: roomState.roomName, text: fakeText });
  };

  const submitPick = (optionId: string) => {
    if (submitted) return;
    socket.emit('doodle-heist-pick', { roomName: roomState.roomName, optionId });
  };

  return (
    <div className="app doodle-heist-player">
      <Header roomCode={roomState.roomName} title="Doodle Heist" />
      <div className="doodle-player-body">
        <p className="doodle-banner">{state?.banner || 'Wait for the host…'}</p>
        {error && <p style={{ color: 'var(--color-error)', textAlign: 'center' }}>{error}</p>}

        {state?.role === 'draw' && !submitted && (
          <>
            <p className="doodle-prompt">Draw: {state.prompt}</p>
            <DrawingCanvas onSubmit={submitDrawing} />
          </>
        )}

        {state?.role === 'draw' && submitted && (
          <p className="doodle-wait">Drawing submitted ✓ Waiting for others…</p>
        )}

        {state?.role === 'wait' && <p className="doodle-wait">Waiting…</p>}

        {state?.role === 'artist' && (
          <p className="doodle-wait">That&apos;s your piece! Others are inventing fake titles…</p>
        )}

        {state?.role === 'artistWait' && (
          <p className="doodle-wait">Your masterpiece is on the TV. Others are voting…</p>
        )}

        {state?.role === 'fake' && (
          <>
            <StrokeReplay strokes={state.strokes || []} size={280} />
            {!submitted ? (
              <div className="doodle-fake-form">
                <input
                  value={fakeText}
                  onChange={(e) => setFakeText(e.target.value)}
                  maxLength={80}
                  placeholder="Write a convincing fake title…"
                  aria-label="Fake title"
                />
                <button type="button" className="doodle-submit" onClick={submitFake}>
                  Submit fake title
                </button>
              </div>
            ) : (
              <p className="doodle-wait">Fake submitted ✓</p>
            )}
          </>
        )}

        {state?.role === 'pick' && (
          <>
            <StrokeReplay strokes={state.strokes || []} size={240} />
            <div className="doodle-pick-list">
              {(state.options || []).map((o) => (
                <button
                  key={o.id}
                  type="button"
                  className={`doodle-pick-btn${o.mine ? ' mine' : ''}`}
                  disabled={submitted}
                  onClick={() => submitPick(o.id)}
                >
                  <strong>{o.letter}.</strong> {o.text}
                  {o.mine ? ' (yours)' : ''}
                </button>
              ))}
            </div>
            {submitted && <p className="doodle-wait">Vote locked ✓</p>}
          </>
        )}

        {(state?.phase === 'reveal' || state?.phase === 'scoreboard') && (
          <p className="doodle-wait">Watch the TV for the reveal…</p>
        )}
      </div>
    </div>
  );
};

export default DoodleHeistPlayerDisplay;
